import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { memo, useEffect } from 'react';
import { isMaxWinnersPerStoreConstraint } from '@lotta/util-lottery-type-guards';
import { useTranslation } from 'react-i18next';
import styles from '../../LotteryForm.module.scss';
import { ControlledInputField } from '../../components/controlled-input-field/ControlledInputField';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer, PropsAreStrings } from '../types';
import type { PurchaseFormType } from '../purchase/PurchaseForm';
import { module as purchaseModule } from '../purchase/PurchaseForm';

export const MODULE_NAME = 'max-winners-per-store';

const maxLimitPerStore = 20000;

export type MaxWinnersPerStoreFormType = PropsAreStrings<
  Static<typeof MaxWinnersPerStoreFormSchema>
>;
export const MaxWinnersPerStoreFormSchema = Type.Object(
  {
    maxNbrWinnersPerStore: Type.Optional(
      Type.Union([
        Type.Integer({
          minimum: 1,
          maximum: maxLimitPerStore,
          errorMessage: {
            type: 'form-error-message-numeric-values',
            minimum: 'form-error-message-minimum-numeric-value',
            maximum: 'form-error-message-maximum-numeric-value',
          },
        }),
        Type.String({ maxLength: 0, minLength: 0 }),
      ])
    ),
  },
  { additionalProperties: false, $id: 'MaxWinnersPerStoreFormSchema' }
);

const defaultValues: MaxWinnersPerStoreFormType = { maxNbrWinnersPerStore: '' };

export const MaxWinnersPerStoreFormTransformer: ModuleTransformer<MaxWinnersPerStoreFormType> = {
  fromPayload: ({ lottery }) => {
    const maxNbrWinnersPerStore = lottery.drawConstraints?.find(isMaxWinnersPerStoreConstraint);

    return {
      maxNbrWinnersPerStore: `${maxNbrWinnersPerStore?.maxNbrWinnersPerStore ?? ''}`,
    };
  },
  toPayload: (formData) => {
    return {
      lottery: {
        drawConstraints:
          formData.maxNbrWinnersPerStore !== ''
            ? [
                {
                  maxNbrWinnersPerStore: parseInt(formData.maxNbrWinnersPerStore, 10),
                },
              ]
            : [],
      },
    };
  },
};

export type MaxWinnersPerStoreFormProps = FormModuleProps<MaxWinnersPerStoreFormType>;

export const MaxWinnersPerStoreForm: React.FC<MaxWinnersPerStoreFormProps> = memo(
  ({ values, onChange, onValidation, getFormState }) => {
    const { control, setValue } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema: MaxWinnersPerStoreFormSchema,
      onChange,
      onValidation,
    });
    const { t } = useTranslation();

    const data = getFormState()[purchaseModule.name] as PurchaseFormType;

    useEffect(() => {
      if (!data.storeIds || data.storeIds.length === 0) {
        setValue(`maxNbrWinnersPerStore`, '');
        control.unregister(`maxNbrWinnersPerStore`);
      }
    }, [data.storeIds, setValue, control]);

    if (!data.storeIds || data.storeIds.length === 0) {
      return null;
    }

    return (
      <form>
        <div className={styles['form-elements']}>
          <ControlledInputField
            control={control}
            labelKey="general-max-number-of-winners-per-store"
            name="maxNbrWinnersPerStore"
            type="number"
            showCharacterLimit={false}
            fieldHelper={{ msg: `${t('lottery-form-max-number-of-winners-per-store-help-text')}` }}
            upperLimit={maxLimitPerStore}
          />
        </div>
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: MaxWinnersPerStoreFormTransformer,
  form: MaxWinnersPerStoreForm,
} as const;
